import { Col, Row } from 'antd';
import { ErrorResult } from 'components/common/ErrorResult';
import { LoadingCard } from 'components/common/LoadingCard';
import { PageTitle } from 'components/common/PageTitle/PageTitle';
import { ActiveContracts } from 'components/dashboard/ActiveContracts';
import { Devices } from 'components/dashboard/Devices';
import { Employees } from 'components/dashboard/employees/Employees';
import { IncidencePendingBanner } from 'components/dashboard/IncidencePendingBanner';
import { UpcomingEventsWidget } from 'components/dashboard/upcoming-events/UpcomingEventsWidget';
import { SliceStatus } from 'constants/enums/slices';
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks';
import { useCallback, useEffect } from 'react';
import {
  doGetDashboardContractKindsCount,
  doGetDashboardDevicesList,
  doGetDashboardEmployeesList,
  doGetDashboardUpcomingBirthdays,
  doGetDashboardUpcomingSeniorityAnniversaries,
} from 'store/slices/dashboardSlice';
import 'styles/sass/dashboard.scss';
import * as Sentry from '@sentry/react';
import notificationHook from 'hooks/notificationHook';
import statusMessages from 'statusMessages';
import { ApiError } from 'types';

const DashboardPage = () => {
  const dispatch = useAppDispatch();
  const {
    employeesList,
    employeesListStatus,
    contractKindsCount,
    contractKindsCountSliceStatus,
    devicesList,
    devicesListSliceStatus,
    upcomingBirthdays,
    upcomingBirthdaysStatus,
    upcomingSeniorityAnniversaries,
    upcomingSeniorityAnniversariesStatus,
  } = useAppSelector((state) => state.dashboard);

  const loadAllDashboardInfo = useCallback(async () => {
    dispatch(doGetDashboardContractKindsCount())
      .unwrap()
      .catch((error) => {
        const currentStatus = (error as ApiError).statusCode;
        Sentry.captureException(error);
        notificationHook({
          type: 'error',
          message: statusMessages.contract[
            currentStatus ? currentStatus : 500
          ] as string,
        });
      });

    dispatch(doGetDashboardDevicesList())
      .unwrap()
      .catch((error) => {
        const currentStatus = (error as ApiError).statusCode;
        Sentry.captureException(error);
        notificationHook({
          type: 'error',
          message: statusMessages.devices[
            currentStatus ? currentStatus : 500
          ] as string,
        });
      });

    dispatch(doGetDashboardEmployeesList())
      .unwrap()
      .catch((error) => {
        const currentStatus = (error as ApiError).statusCode;
        Sentry.captureException(error);
        notificationHook({
          type: 'error',
          message: statusMessages.user[
            currentStatus ? currentStatus : 500
          ] as string,
        });
      });

    dispatch(doGetDashboardUpcomingBirthdays())
      .unwrap()
      .catch((error) => {
        const currentStatus = (error as ApiError).statusCode;
        Sentry.captureException(error);
        notificationHook({
          type: 'error',
          message:
            (statusMessages.user[
              currentStatus ? currentStatus : 500
            ] as string) || 'No fue posible cargar el widget de cumpleaños.',
        });
      });

    dispatch(doGetDashboardUpcomingSeniorityAnniversaries())
      .unwrap()
      .catch((error) => {
        const currentStatus = (error as ApiError).statusCode;
        Sentry.captureException(error);
        notificationHook({
          type: 'error',
          message:
            (statusMessages.user[
              currentStatus ? currentStatus : 500
            ] as string) ||
            'No fue posible cargar el widget de aniversario laboral.',
        });
      });
  }, [dispatch]);

  useEffect(() => {
    loadAllDashboardInfo();
  }, [loadAllDashboardInfo]);

  const isUpdating = [
    employeesListStatus,
    contractKindsCountSliceStatus,
    devicesListSliceStatus,
  ].includes(SliceStatus.Updating);

  const hasCriticalDashboardError = [
    employeesListStatus,
    contractKindsCountSliceStatus,
    devicesListSliceStatus,
  ].every((status) => status === SliceStatus.Error);

  return (
    <>
      <PageTitle>{`Dashboard`}</PageTitle>
      <h2 className="title">Dashboard</h2>
      <Row className="mt-8">
        <Col xl={24} xxl={18}>
          <IncidencePendingBanner audience="admin" />
          {isUpdating ? (
            <LoadingCard />
          ) : (
            <>
              {contractKindsCount && (
                <ActiveContracts contractKinds={contractKindsCount} />
              )}
              <Row className="mt-5" gutter={[16, 16]}>
                <Col xs={24} xl={12}>
                  <UpcomingEventsWidget
                    dataCy="dashboard-birthdays-widget"
                    emptyMessage="No hay cumpleaños próximos en esta semana ni en la siguiente."
                    errorMessage="No fue posible cargar los cumpleaños próximos."
                    events={upcomingBirthdays}
                    kind="birthday"
                    status={upcomingBirthdaysStatus}
                    title="Cumpleaños"
                  />
                </Col>
                <Col xs={24} xl={12}>
                  <UpcomingEventsWidget
                    dataCy="dashboard-anniversaries-widget"
                    emptyMessage="No hay aniversarios laborales próximos en esta semana ni en la siguiente."
                    errorMessage="No fue posible cargar los aniversarios laborales próximos."
                    events={upcomingSeniorityAnniversaries}
                    kind="anniversary"
                    status={upcomingSeniorityAnniversariesStatus}
                    title="Aniversario laboral"
                  />
                </Col>
              </Row>
              <Row className="mt-5" gutter={[16, 16]}>
                <Col xs={24} xl={12}>
                  {employeesList && (
                    <Employees todaysScheduleByEmployee={employeesList} />
                  )}
                </Col>
                <Col xs={24} xl={12}>
                  {devicesList && <Devices devices={devicesList} />}
                </Col>
              </Row>
              {hasCriticalDashboardError && (
                <ErrorResult title="No fue posible cargar el dashboard" />
              )}
            </>
          )}
        </Col>
      </Row>
    </>
  );
};

export default DashboardPage;
